import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const faqs = [
  { q: 'How do I file a claim?', a: 'Call our office at 606-269-1190 or email John@summitappalachia.com. For after-hours emergencies, call the same number and follow the prompts.' },
  { q: 'How long does the claims process take?', a: 'Most straightforward claims are resolved within 10–15 business days. Complex claims may take longer depending on the circumstances.' },
  { q: 'What documents do I need?', a: 'Typically you\'ll need your policy number, a description of the incident, photos of damage, a police report (if applicable), and any repair estimates.' },
  { q: 'Will filing a claim raise my rates?', a: 'Not necessarily. Rate impacts depend on the type of claim and your history. Our agents can walk you through the potential impact before you file.' },
  { q: 'Can I choose my own repair contractor?', a: 'Yes. While we have preferred contractors, you are free to choose your own licensed contractor and we\'ll work directly with them.' },
];

export default function Claims() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    document.title = 'File an Insurance Claim | Summit Appalachia Insurance KY TN VA';
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${import.meta.env.VITE_SITE_URL}/claims#webpage`,
          url: `${import.meta.env.VITE_SITE_URL}/claims`,
          name: 'File an Insurance Claim | Summit Appalachia Insurance KY TN VA',
          description: 'File a business insurance claim with Summit Appalachia Insurance. Our dedicated claims team serves Kentucky, Tennessee, Virginia, and West Virginia. Call 606-269-1190.',
          isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
        },
        {
          '@type': 'FAQPage',
          '@id': `${import.meta.env.VITE_SITE_URL}/claims#faq`,
          mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.a,
            },
          })),
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-claims';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-claims')?.remove(); };
  }, []);

  return (
    <div className="font-sans bg-[#0a0a0a]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/7385fbf1b4092d1d2f08a4d607cd0b32.jpg"
            alt="Claims" className="w-full h-full object-cover object-top opacity-30" />
          <div className="absolute inset-0 bg-[#0a0a0a]/70" />
        </div>
        <div className="relative z-10 text-center px-6 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#c9a84c] cursor-pointer">Home</Link> / Claims
          </p>
          <h1 className="font-serif text-5xl md:text-7xl text-white uppercase mb-4">File a Claim</h1>
          <p className="text-white/50 font-sans text-lg max-w-xl mx-auto">We&apos;re here for you. Our dedicated claims team makes the process as smooth and fast as possible.</p>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-[#111111] border-y border-[#c9a84c]/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#c9a84c] rounded-md p-10 flex flex-col items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-black/20 rounded-md mb-5">
                <i className="ri-file-add-line text-black text-2xl" />
              </div>
              <h3 className="font-serif text-2xl text-black uppercase mb-3">File a New Claim</h3>
              <p className="text-black/70 font-sans text-sm mb-6 leading-relaxed">Start the claims process by calling us or sending a message. We&apos;ll guide you every step of the way.</p>
              <a href="tel:6062691190" className="mt-auto px-6 py-3 bg-black text-[#c9a84c] text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#111] transition-colors whitespace-nowrap cursor-pointer font-sans">
                Call 606-269-1190
              </a>
            </div>
            <div className="bg-[#1a1a1a] border border-[#c9a84c]/20 rounded-md p-10 flex flex-col items-start">
              <div className="w-12 h-12 flex items-center justify-center bg-[#c9a84c]/10 rounded-md mb-5">
                <i className="ri-search-eye-line text-[#c9a84c] text-2xl" />
              </div>
              <h3 className="font-serif text-2xl text-white uppercase mb-3">Track Existing Claim</h3>
              <p className="text-white/50 font-sans text-sm mb-6 leading-relaxed">Already filed? Check the status of your claim or speak with your assigned adjuster directly.</p>
              <a href="mailto:John@summitappalachia.com" className="mt-auto px-6 py-3 border border-[#c9a84c] text-[#c9a84c] text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#c9a84c] hover:text-black transition-colors whitespace-nowrap cursor-pointer font-sans">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#111111] border-t border-[#c9a84c]/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <span className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.3em] font-sans">Common Questions</span>
            <h2 className="font-serif text-4xl text-white mt-3 uppercase">Claims FAQs</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-[#c9a84c]/15 rounded-md overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#1a1a1a] transition-colors">
                  <span className="font-sans font-bold text-white text-sm pr-4 uppercase tracking-wide">{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`text-[#c9a84c] text-lg ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-[#c9a84c]/10">
                    <p className="text-white/50 text-sm font-sans leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
